import React, { useEffect, useState } from "react";
import {
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Spin,
  Table,
  Typography,
  Upload,
  message,
} from "antd";
import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  PlusOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import {
  listPricingSchedulesAction,
  createPricingScheduleAction,
  updatePricingScheduleAction,
  deletePricingScheduleAction,
} from "../../../store/company/companyActions";
import { downloadFile } from "../../../utils/downloadFile";

const { Text, Link } = Typography;
const { TextArea } = Input;

const CompanyPricingSchedulesPanel = () => {
  const dispatch = useDispatch();
  const companyKey = useSelector(
    (state) => state.actor?.details?.data?.BrokerId,
  );
  const { data: pricingSchedules = [], loading = false } = useSelector(
    (state) => state.company?.pricingSchedules || {},
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = Form.useForm();
  const [file, setFile] = useState(null);

  useEffect(() => {
    if (companyKey) {
      dispatch(listPricingSchedulesAction(companyKey));
    }
  }, [companyKey, dispatch]);

  const handleAdd = () => {
    setEditingRecord(null);
    form.resetFields();
    setFile(null);
    // Set default effective date to today
    form.setFieldsValue({
      effectiveDate: dayjs().format("YYYY-MM-DD")
    });
    setIsModalOpen(true);
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    form.setFieldsValue({
      documentName: record.DocumentName,
      effectiveDate: dayjs(record.EffectiveDate).format("YYYY-MM-DD"),
      notes: record.Notes,
    });
    setFile(null);
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setEditingRecord(null);
    form.resetFields();
    setFile(null);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (!editingRecord && !file) {
        message.error("File is required when creating a new pricing schedule.");
        return;
      }

      const formData = new FormData();
      
      if (file) {
        formData.append("file", file);
      }

      formData.append("documentName", values.documentName);
      formData.append("effectiveDate", values.effectiveDate);
      formData.append("notes", values.notes || "");

      // DEBUG: Log what we're sending
      console.log("=== FormData Contents ===");
      console.log("File:", file);
      console.log("Document Name:", values.documentName);
      console.log("Effective Date:", values.effectiveDate);
      console.log("Notes:", values.notes);
      for (let pair of formData.entries()) {
        console.log(pair[0], pair[1]);
      }

      if (editingRecord) {
        formData.append("companyPricingScheduleKey", editingRecord.CompanyPricingScheduleKey);
        dispatch(updatePricingScheduleAction(formData, companyKey));
      } else {
        dispatch(createPricingScheduleAction(formData, companyKey));
      }

      handleCancel();
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  const handleDelete = (record) => {
    dispatch(
      deletePricingScheduleAction({
        CompanyPricingScheduleKey: record.CompanyPricingScheduleKey,
        CompanyKey: companyKey,
      }),
    );
  };

  const handleDownload = async (record) => {
    try {
      const { getApiFetch } = await import("../../../api/getApiFetch");
      const apiFetch = getApiFetch();
      
      const url = `${process.env.REACT_APP_API_URI}/Company/DownloadPricingSchedule?companyKey=${companyKey}&fileName=${encodeURIComponent(record.FileName)}`;
      
      const response = await apiFetch(url, { method: "GET" });
      
      if (!response.ok) {
        message.error("Failed to download file.");
        return;
      }

      const blob = await response.blob();
      downloadFile(blob, record.FileName);
    } catch (error) {
      console.error("Download error:", error);
      message.error("Failed to download file.");
    }
  };

  const uploadProps = {
    beforeUpload: (file) => {
      const isValidType = [
        "application/pdf",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ].includes(file.type);

      if (!isValidType) {
        message.error("Only PDF, Excel, and Word files are allowed.");
        return false;
      }

      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error("File must be smaller than 10MB.");
        return false;
      }

      return true;
    },
    customRequest: ({ file, onSuccess }) => {
      setFile(file);
      
      // Auto-populate document name from filename (without extension) if not editing
      if (!editingRecord) {
        const fileNameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
        form.setFieldsValue({ documentName: fileNameWithoutExt });
      }
      
      onSuccess("ok");
    },
    onRemove: () => {
      setFile(null);
      // Clear document name when removing file during creation
      if (!editingRecord) {
        form.setFieldsValue({ documentName: "" });
      }
    },
    fileList: file ? [{ uid: '-1', name: file.name, status: 'done' }] : [],
  };

  const columns = [
    {
      title: "Document Name",
      dataIndex: "DocumentName",
      key: "DocumentName",
      width: 350, 
      render: (text, record) => (
        <Link onClick={() => handleDownload(record)}>
          <DownloadOutlined style={{ marginRight: 8 }} />
          {text}
        </Link>
      ),
    },
    {
      title: "Effective Date",
      dataIndex: "EffectiveDate",
      key: "EffectiveDate",
      width: 140,
      render: (value) => dayjs(value).format("DD-MMM-YYYY"),
    },
    {
      title: "Uploaded Date",
      dataIndex: "UploadedDate",
      key: "UploadedDate",
      width: 160,
      render: (value) => dayjs(value).format("DD-MMM-YYYY HH:mm"),
    },
    {
      title: "Uploaded By",
      dataIndex: "UploadedBy",
      key: "UploadedBy",
      width: 150,
    },
    {
      title: "Notes",
      dataIndex: "Notes",
      key: "Notes",
      ellipsis: true,
      render: (text) => text || null,  
    },
    {
      title: "",
      key: "actions",
      width: 100,
      render: (_, record) => (
        <Space size={4}>
          <Button
            size="small"
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Delete this pricing schedule?"
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              size="small"
              type="link"
              danger
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Spin spinning={loading}>
      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        <Button
          size="small"
          icon={<PlusOutlined />}
          onClick={handleAdd}
        >
          Add Pricing Schedule
        </Button>
        <Table
          size="small"
          columns={columns}
          dataSource={pricingSchedules.map((s) => ({
            ...s,
            key: s.CompanyPricingScheduleKey,
          }))}
          pagination={false}
          bordered
          locale={{ emptyText: "No pricing schedules uploaded." }}
        />
      </Space>

      <Modal
        title={editingRecord ? "Edit Pricing Schedule" : "Add Pricing Schedule"}
        open={isModalOpen}
        onOk={handleSubmit}
        onCancel={handleCancel}
        okText={editingRecord ? "Update" : "Create"}
        width={600}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            label="Document Name"
            name="documentName"
            rules={[{ required: true, message: "Document name is required" }]}
          >
            <Input placeholder="e.g., 2026 Pricing Schedule" />
          </Form.Item>

          <Form.Item
            label="Effective Date"
            name="effectiveDate"
            rules={[{ required: true, message: "Effective date is required" }]}
          >
            <Input 
              type="date" 
              placeholder="Select date"
            />
          </Form.Item>

          <Form.Item
            label={editingRecord ? "Replace File (optional)" : "File"}
            required={!editingRecord}
          >
            <Upload {...uploadProps} maxCount={1}>
              <Button icon={<UploadOutlined />}>
                {editingRecord ? "Choose New File" : "Choose File"}
              </Button>
            </Upload>
            {!editingRecord && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                PDF, Excel, or Word files only (max 10MB)
              </Text>
            )}
          </Form.Item>

          <Form.Item label="Notes" name="notes">
            <TextArea rows={3} placeholder="Optional notes" maxLength={500} />
          </Form.Item>
        </Form>
      </Modal>
    </Spin>
  );
};

export default CompanyPricingSchedulesPanel;